import { z } from 'zod';

const textStyleSchema = z.object({
  fontFamily: z.string(),
  fontSize: z.number().finite().positive(),
  fontWeight: z.number().finite(),
  italic: z.boolean(),
  underline: z.boolean(),
  align: z.enum(['left', 'center', 'right']),
  fill: z.string(),
  background: z.string(),
  border: z.string(),
  borderWidth: z.number().finite().nonnegative(),
  radius: z.number().finite().nonnegative(),
  padding: z.number().finite().nonnegative(),
  lineHeight: z.number().finite().positive(),
});

const characterStyleSchema = z.object({
  skin: z.string(),
  hair: z.string(),
  top: z.string(),
  bottom: z.string(),
  shoes: z.string(),
  expression: z.enum(['happy', 'neutral', 'sad', 'angry', 'surprised']),
  pose: z.enum(['stand', 'wave', 'point', 'celebrate', 'sit']),
  view: z.enum(['front', 'side']),
});

const appearanceSchema = z.object({
  fill: z.string(),
  stroke: z.string(),
  strokeWidth: z.number().finite().nonnegative(),
});

const elementSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(['character', 'prop', 'text', 'bubble', 'shape', 'image']),
  name: z.string(),
  assetId: z.string().optional(),
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().positive(),
  height: z.number().finite().positive(),
  rotation: z.number().finite(),
  opacity: z.number().finite().min(0).max(1),
  flipX: z.boolean(),
  flipY: z.boolean(),
  locked: z.boolean(),
  hidden: z.boolean(),
  zIndex: z.number().finite(),
  text: z.string().optional(),
  textStyle: textStyleSchema.optional(),
  character: characterStyleSchema.optional(),
  bubbleStyle: z.enum(['speech', 'thought', 'shout', 'caption', 'whisper']).optional(),
  shapeStyle: z.enum(['rectangle', 'ellipse', 'star', 'arrow', 'line', 'heart']).optional(),
  appearance: appearanceSchema.optional(),
  dataUrl: z.string().optional(),
  alt: z.string().optional(),
});

const cellSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  description: z.string(),
  durationMs: z.number().finite().min(250).max(120_000),
  audioDataUrl: z.string().optional(),
  scene: z.object({
    assetId: z.string(),
    time: z.enum(['day', 'sunset', 'night']),
    weather: z.enum(['clear', 'rain', 'snow']),
    intensity: z.number().finite().min(0).max(1),
  }),
  elements: z.array(elementSchema).max(1000),
});

export const projectSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().min(1),
  title: z.string().min(1).max(200),
  createdAt: z.string(),
  updatedAt: z.string(),
  layout: z.object({
    columns: z.number().int().min(1).max(6),
    aspectRatio: z.enum(['cinematic', 'landscape', 'square', 'portrait']),
    showTitles: z.boolean(),
    showDescriptions: z.boolean(),
    snapToGrid: z.boolean(),
    gridSize: z.number().int().min(1).max(200),
    background: z.string(),
  }),
  cells: z.array(cellSchema).min(1).max(100),
});

export type ParsedProject = z.infer<typeof projectSchema>;
